/**
 * Column definitions for the shareholder table.
 *
 * Same module shape as `tasks/table.ts` and the three before it. The Name column
 * is not here: always shown, always first, the only one that renders two lines.
 *
 * Every figure comes in as a `Holding` — the position derived once per render in
 * the workspace — rather than being computed per cell. Shares, percentage and
 * status each walk the transfer ledger, and doing that three times per row per
 * column was the shape this file had before transfers existed.
 *
 * Pers.nr. is not a column. It was one until the 14 Aug round, masked to the
 * last four; the design took it off list views, and a masked column only ever
 * said "on file", which the record says better. See `types/ownership.ts`.
 */
import type {
  Cell as SharedCell,
  ColumnFilters as SharedColumnFilters,
  ColumnMeta,
  TotalsRow,
} from '@/shared/components/DataTable'
import {
  EMPTY_HOLDING,
  HOLDER_STATUSES,
  holderStatusStyle,
  effectivePrice,
  formatOrgNumber,
  unpaidExerciseShares,
  type Holding,
  type Issuance,
  type Shareholder,
} from '@/types/ownership'
import { fmtKrPrice, fmtKrSum } from '@/shared/util/money'
import { formatDate } from '@/shared/util/dates'

export type ColumnKey =
  | 'status'
  | 'shares'
  | 'ownership'
  | 'options'
  | 'contact'
  | 'linked'
  | 'kind'
  | 'orgNumber'
  | 'phone'
  | 'address'
  | 'country'
  | 'entryDate'
  | 'shareSeries'
  | 'files'
  | 'elsewhere'

export const COLUMN_META: Record<ColumnKey, ColumnMeta> = {
  status: { label: 'Status', width: 1 },
  shares: { label: 'Shares', width: 0.8 },
  ownership: { label: 'Ownership', width: 0.8 },
  options: { label: 'Options', width: 0.8 },
  contact: { label: 'Contact', width: 1.3 },
  /** The same person in the sales book or the ambassador programme. */
  linked: { label: 'Linked to', width: 1.3 },
  /**
   * That documents exist, not which — decision 54 keeps the list in the record.
   *
   * Off by default. It is one of the fields decision 55's "All columns" width
   * will carry, and until that toggle exists it is reachable through the column
   * picker rather than pushed into a seven-column essential read the design
   * draws as six.
   */
  files: { label: 'Files', width: 0.6 },
  /**
   * Whether this holder is also a customer, a guide, an ambassador or a lead.
   *
   * Computed by `scripts/matchShareholders.ts` and read here, never entered. In
   * the All-columns width because it answers a question about the register as a
   * whole — 47 of the 144 owners pay for the product — which is not what the
   * six essential columns are for.
   */
  elsewhere: { label: 'Elsewhere', width: 1.2 },
  /* The four below join `files` in decision 55's "All columns" width. Every one
     is a field the register already holds; none is in the essential read. */
  kind: { label: 'Kind', width: 0.7 },
  orgNumber: { label: 'Org.nr', width: 0.9 },
  phone: { label: 'Phone', width: 1 },
  address: { label: 'Address', width: 1.4 },
  /**
   * The three decision 77 sends to the wide table, and each is a register
   * field rather than a contact detail.
   *
   * `country` is the one that decides which register a holder belongs to;
   * `entryDate` is the day they came onto this one, which is the column an
   * aksjeeierbok is read down; `shareSeries` is the share numbers the holding
   * covers, which is the only field on the record that says *which* shares.
   */
  country: { label: 'Country', width: 0.8 },
  entryDate: { label: 'On the register since', width: 1 },
  shareSeries: { label: 'Share series', width: 1 },
}

export const COLUMN_KEYS = Object.keys(COLUMN_META) as ColumnKey[]

/**
 * The two widths decision 55 draws.
 *
 * `ESSENTIAL_COLUMNS` is the seven-column read — the identity column plus these
 * six. `ALL_COLUMNS` widens to every field the register holds and scrolls
 * sideways.
 *
 * **Personnummer is in neither**, and that is not an oversight to be corrected
 * by somebody adding it later: decision 14 keeps it out of a list view whatever
 * the width, and the note under the wide table says where it does live.
 */
export const DEFAULT_COLUMNS: ColumnKey[] = [
  'status',
  'shares',
  'ownership',
  'options',
  'contact',
  'linked',
]

/** The essential read, by its other name — decision 55's narrower width. */
export const ESSENTIAL_COLUMNS: ColumnKey[] = DEFAULT_COLUMNS

/** Every field the register holds, personnummer excepted — see above. */
export const ALL_COLUMNS: ColumnKey[] = [
  'kind',
  ...DEFAULT_COLUMNS,
  'orgNumber',
  'phone',
  'address',
  'country',
  'entryDate',
  'shareSeries',
  'files',
  'elsewhere',
]

/** `TableKey` rather than `ColumnKey`: a round header is sortable too. */
export type SortKey = ColumnKey | RoundColumnKey | 'name'

/** Holdings read largest-first; that is what the register is scanned for. */
export const DESCENDING_FIRST: readonly string[] = ['shares', 'ownership', 'options']

export const FILTERABLE_COLUMNS: ColumnKey[] = ['status']

export type ColumnFilters = SharedColumnFilters<ColumnKey>
export type Cell = SharedCell<ColumnKey>

/** Positions, keyed by shareholder id — what `holdings()` returns. */
export type HoldingMap = Map<string, Holding>

export const holdingOf = (h: HoldingMap, s: Shareholder): Holding =>
  h.get(s.id) ?? EMPTY_HOLDING

export function filterValue(s: Shareholder, key: ColumnKey, h: HoldingMap): string {
  return key === 'status' ? holdingOf(h, s).status : ''
}

export function passesFilters(
  s: Shareholder,
  filters: ColumnFilters,
  h: HoldingMap,
): boolean {
  return FILTERABLE_COLUMNS.every((key) => {
    const selected = filters[key]
    if (!selected || selected.length === 0) return true
    return selected.includes(filterValue(s, key, h))
  })
}

const nb = (n: number) => n.toLocaleString('nb-NO')

/** Two decimals, Norwegian comma — the design's `12,34%`. */
export function formatPct(pct: number | null): string {
  return pct === null ? '—' : `${pct.toFixed(2).replace('.', ',')}%`
}

/**
 * Shares this holder has said they want in a round that has not closed — 79.
 *
 * Filtered by the round's status where the issuance list is to hand, rather than
 * trusting that a `subscriptions` entry always names an open round. It normally
 * does — a subscription becomes a purchase when the round closes — but a round
 * that closed while somebody had the screen open would otherwise show its
 * interest for ever, and this figure sits directly under an issued one.
 */
export function subscribedShares(s: Shareholder, issuances?: Issuance[]): number {
  const open = issuances && new Set(issuances.filter((i) => i.status === 'ongoing').map((i) => i.id))
  return (s.subscriptions ?? [])
    .filter((x) => !open || open.has(x.issuanceId))
    .reduce((n, x) => n + x.shares, 0)
}

export function buildCells(
  s: Shareholder,
  columns: ColumnKey[],
  h: HoldingMap,
  issuances?: Issuance[],
): Cell[] {
  const pos = holdingOf(h, s)
  const subscribed = subscribedShares(s, issuances)
  return columns.map((key): Cell => {
    switch (key) {
      case 'status': {
        const st = holderStatusStyle(pos.status)
        return { key, badge: true, text: st.label, bg: st.bg, color: st.color }
      }
      case 'shares':
        return {
          key,
          badge: false,
          text: pos.shares > 0 ? nb(pos.shares) : '—',
          // Decision 79: what the status pill cannot say. A subscriber to an
          // open round holds nothing and is correctly a Potential investor, but
          // the row should not read as somebody who has done nothing.
          sub: subscribed > 0 ? `${nb(subscribed)} subscribed, not issued` : undefined,
          // Only when the two differ, which is only after a settled transfer:
          // saying "15 000 issued" on every untouched row would be noise.
          title:
            pos.issued !== pos.shares
              ? `${nb(pos.issued)} issued, ${nb(pos.shares)} held after transfers`
              : undefined,
        }
      case 'ownership':
        return { key, badge: false, text: formatPct(pos.pct) }
      case 'options': {
        /**
         * A grant that has been exercised and not yet paid for — off-script 15.
         *
         * `pos.options` counts *active* grants only, which is right: an
         * exercised grant is no longer an option. But the shares it will become
         * do not exist either until the strike price lands, so the row read
         * "—" in Options and nothing in Shares, and a holder mid-exercise looked
         * like a holder with nothing. Both facts are true and neither is
         * visible; the second line says the one the em dash was hiding.
         */
        const unpaid = unpaidExerciseShares(s)
        return {
          key,
          badge: false,
          text: pos.options > 0 ? nb(pos.options) : '—',
          sub: unpaid > 0 ? `${nb(unpaid)} exercised, awaiting payment` : undefined,
          title:
            pos.options > 0
              ? 'Active grants only — see the record for the full split'
              : unpaid > 0
                ? 'Exercised, but the strike price has not landed, so these are not shares yet'
                : undefined,
        }
      }
      case 'contact':
        return { key, badge: false, text: s.email || s.phone || '—' }
      case 'linked':
        return {
          key,
          badge: false,
          text: s.linkedLabel || '—',
          // The link is the interesting part of the row: this person is also in
          // the pipeline or the programme, which is a thing to know before
          // anybody writes to them.
          color: s.linkedLabel ? 'var(--color-primary)' : undefined,
        }
      case 'kind':
        return { key, badge: false, text: s.kind === 'company' ? 'Company' : 'Person' }
      case 'orgNumber':
        // Public information from Enhetsregisteret, so it is not masked — and a
        // person has none, which is an em dash rather than an empty cell.
        return { key, badge: false, text: s.orgNumber ? formatOrgNumber(s.orgNumber) : '—' }
      case 'phone':
        return { key, badge: false, text: s.phone || '—' }
      case 'address':
        return { key, badge: false, text: s.address || '—' }
      case 'country':
        return { key, badge: false, text: s.country || '—' }
      case 'entryDate':
        // An em dash rather than a blank: the register holds no entry date for
        // the holders assembled from Folkeinvest's file, and that is a gap in
        // the source worth seeing (11).
        return { key, badge: false, text: s.entryDate ? formatDate(s.entryDate) : '—' }
      case 'shareSeries':
        return {
          key,
          badge: false,
          text: s.shareSeries || '—',
          // 77 / A4.16 — monospaced wherever it appears, so the digits line up
          // down the column and a range reads as a range.
          mono: !!s.shareSeries,
          title: s.shareSeries ? 'The share numbers this holding covers' : undefined,
        }
      case 'elsewhere': {
        const m = s.matches
        // PRO first: somebody running a guide company on our own product is a
        // different fact from somebody who bought the app.
        const bits = [
          m?.proMember && 'PRO',
          m?.appUser?.entitlements.length ? 'Paying' : m?.appUser && 'App',
          m?.ambassador && 'Ambassador',
          m?.partner && 'Partner',
          m?.lead && 'Lead',
        ].filter(Boolean) as string[]
        return {
          key,
          badge: false,
          text: bits.join(' · ') || '—',
          title: m?.proMember
            ? `${m.proMember.role} at ${m.proMember.companyName}`
            : m?.lead
              ? m.lead.name
              : undefined,
        }
      }
      case 'files': {
        const n = s.attachments?.length ?? 0
        return {
          key,
          badge: false,
          // A bare 0 on a register that has filed nothing would read as
          // a measurement; an em dash reads as nothing filed (11).
          text: n > 0 ? `📎 ${n}` : '—',
          title: n > 0 ? 'Open the record to see what is filed' : undefined,
        }
      }
    }
  })
}

export function sortValue(
  s: Shareholder,
  sortBy: SortKey,
  h: HoldingMap,
  issuances?: Issuance[],
): string | number | null {
  const pos = holdingOf(h, s)
  /**
   * A round header sorts by that round, not by name.
   *
   * Without this the `default` below caught every `round:…` key and sorted the
   * table by name while showing an arrow on the round column — a control that
   * looks like it worked and did nothing.
   */
  const round = parseRoundKey(sortBy)
  if (round) {
    const issuance = issuances?.find((i) => i.id === round.issuanceId)
    if (!issuance) return null
    const f = roundFigures(s, issuance)
    // Null rather than nought for a holder who took no part, so they sort to the
    // end in both directions instead of into the middle of the zeroes.
    if (f.shares === 0) return null
    // A row with two prices sorts by the lowest of them rather than falling to
    // the end: it is not a row without a price.
    return round.part === 'shares'
      ? f.shares
      : round.part === 'amount'
        ? f.amount
        : (f.distinct[0] ?? null)
  }
  switch (sortBy) {
    case 'status':
      return HOLDER_STATUSES.findIndex((x) => x.key === pos.status)
    case 'shares':
      return pos.shares || null
    case 'ownership':
      return pos.pct
    case 'options':
      return pos.options || null
    case 'contact':
      return (s.email || s.phone).toLowerCase() || null
    case 'linked':
      return s.linkedLabel.toLowerCase() || null
    case 'kind':
      return s.kind
    case 'orgNumber':
      return s.orgNumber || null
    case 'phone':
      return s.phone.toLowerCase() || null
    case 'address':
      return s.address.toLowerCase() || null
    case 'country':
      return s.country?.toLowerCase() || null
    case 'entryDate':
      // The ISO string sorts as a date, which is the whole reason it is stored
      // as one — see `types/ownership.ts`.
      return s.entryDate || null
    case 'shareSeries':
      // Sorted by where the series starts, not as text: `17106–30000` before
      // `9001–17105` is what a string comparison gives and it is nonsense on a
      // column of share numbers.
      return parseInt(s.shareSeries ?? '', 10) || null
    case 'elsewhere':
      // Sorted by how much it matters, not alphabetically: a PRO admin above a
      // paying user above an account above nothing.
      return s.matches?.proMember
        ? 4
        : s.matches?.appUser?.entitlements.length
          ? 3
          : s.matches?.appUser
            ? 2
            : s.matches?.lead || s.matches?.ambassador || s.matches?.partner
              ? 1
              : 0
    case 'files':
      return String(s.attachments?.length ?? 0)
    default:
      return s.name.toLowerCase()
  }
}

export function sortShareholders(
  rows: Shareholder[],
  sortBy: SortKey,
  dir: 'asc' | 'desc',
  h: HoldingMap,
  issuances?: Issuance[],
): Shareholder[] {
  const sign = dir === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const av = sortValue(a, sortBy, h, issuances)
    const bv = sortValue(b, sortBy, h, issuances)
    if (av === null && bv === null) return a.name.localeCompare(b.name)
    if (av === null) return 1
    if (bv === null) return -1
    if (av < bv) return -sign
    if (av > bv) return sign
    return a.name.localeCompare(b.name)
  })
}

/* -------------------------------------------------------------------------- */
/* Three columns per round — decisions 81, 79 and 78                          */
/* -------------------------------------------------------------------------- */

/**
 * The round columns are not in `ColumnKey`, and that is deliberate.
 *
 * `ColumnKey` is a closed union because the column picker, the stored column
 * preference and `useTableState`'s stale-key pruning all depend on knowing every
 * legal key up front. A round column cannot satisfy that: the rounds arrive from
 * Firestore after the first render, so a stored preference naming
 * `round:iss-4:shares` would be pruned as stale on load and lost.
 *
 * So these belong to the **width**, not to the picker — which is what decision
 * 81 says in the first place: *three columns per round in All columns*. They are
 * appended when the wide width is on and derived from the issuance list every
 * render, so a new round appears without anybody choosing it and a deleted one
 * cannot linger in a saved preference.
 */
export type RoundPart = 'shares' | 'price' | 'amount'
export type RoundColumnKey = `round:${string}:${RoundPart}`

/** Every key the shareholder table can render — the static set plus the rounds. */
export type TableKey = ColumnKey | RoundColumnKey

const roundKey = (issuanceId: string, part: RoundPart): RoundColumnKey =>
  `round:${issuanceId}:${part}`

/** `null` for a static column, so a caller can branch on one call. */
export function parseRoundKey(key: string): { issuanceId: string; part: RoundPart } | null {
  if (!key.startsWith('round:')) return null
  const rest = key.slice('round:'.length)
  const cut = rest.lastIndexOf(':')
  if (cut <= 0) return null
  const part = rest.slice(cut + 1)
  if (part !== 'shares' && part !== 'price' && part !== 'amount') return null
  return { issuanceId: rest.slice(0, cut), part }
}

/**
 * Oldest first, sorted on read — decision 18, and the same order the cards and
 * the capital-history chart use, so the table cannot disagree with them.
 */
export const roundsInOrder = (issuances: Issuance[]): Issuance[] =>
  [...issuances].sort((a, b) => a.date.localeCompare(b.date) || a.name.localeCompare(b.name))

export function roundColumns(issuances: Issuance[]): RoundColumnKey[] {
  return roundsInOrder(issuances).flatMap((i) => [
    roundKey(i.id, 'shares'),
    roundKey(i.id, 'price'),
    roundKey(i.id, 'amount'),
  ])
}

/**
 * Headers carry the round's own name, because three unlabelled `Price` columns
 * next to each other are unreadable and the sort button needs to say what it
 * sorts. An open round says so in its header rather than only in the colour, so
 * the distinction survives for anybody who cannot see the grey.
 */
export function roundColumnMeta(issuances: Issuance[]): Record<RoundColumnKey, ColumnMeta> {
  const out = {} as Record<RoundColumnKey, ColumnMeta>
  for (const i of roundsInOrder(issuances)) {
    const open = i.status === 'ongoing'
    out[roundKey(i.id, 'shares')] = {
      label: open ? `${i.name} · interest` : `${i.name} · shares`,
      width: 0.85,
    }
    out[roundKey(i.id, 'price')] = { label: `${i.name} · price`, width: 0.9 }
    out[roundKey(i.id, 'amount')] = { label: `${i.name} · amount`, width: 1 }
  }
  return out
}

/**
 * What one holder did in one round, as three numbers that agree with each other.
 *
 * A completed round reads `purchases`; an open one reads `subscriptions` and
 * nothing else — decision 79, and the reason `Shareholder` keeps the two arrays
 * apart in the first place. A promise of 150 shares must never reach a column
 * that is summed into a holding.
 *
 * ## No averages, anywhere — decision 78
 *
 * A holder can take shares twice in the same round at two agreed prices, and
 * Emisjon 4 carries three. The tempting cell is `amount ÷ shares`, which at
 * least multiplies out — but 78 rules it out in as many words: *"a weighted
 * average is a figure nobody agreed to"*. So the row shows the price when there
 * is one, and **says how many there are** when there is more than one, with the
 * prices themselves in the tooltip. The amount column stays exact either way,
 * because it is summed from the lines rather than derived from a price.
 */
interface RoundFigures {
  shares: number
  amount: number
  /** The one price behind this holding, or null where there is not exactly one. */
  price: number | null
  /** Every distinct price behind it, so a row with several can name them. */
  distinct: number[]
  /** Any of it at a price this holder agreed, rather than the round's — 78. */
  agreed: boolean
  /** Interest in a round that has not closed, so it counts towards nothing — 79. */
  interest: boolean
  /** More than one price behind the average, so the tooltip can say so. */
  blended: boolean
}

export function roundFigures(s: Shareholder, issuance: Issuance): RoundFigures {
  const interest = issuance.status === 'ongoing'
  const lines: { shares: number; pricePerShare?: number | null }[] = interest
    ? (s.subscriptions ?? []).filter((x) => x.issuanceId === issuance.id)
    : s.purchases.filter((p) => p.issuanceId === issuance.id)

  let shares = 0
  let amount = 0
  let agreed = false
  const prices = new Set<number>()
  for (const line of lines) {
    const price = effectivePrice(line, issuance)
    shares += line.shares
    if (price !== null) {
      amount += line.shares * price
      prices.add(price)
    }
    if (line.pricePerShare !== null && line.pricePerShare !== undefined) agreed = true
  }

  const distinct = [...prices].sort((a, b) => a - b)
  return {
    shares,
    amount,
    // Null rather than nought where no price is recorded anywhere: the round
    // may have no price yet, and kr 0,00 is a price somebody agreed (80).
    price: distinct.length === 1 ? distinct[0]! : null,
    distinct,
    agreed,
    interest,
    blended: distinct.length > 1,
  }
}

/**
 * The grey of an open round — decision 79.
 *
 * One constant rather than a colour typed at each of the three call sites, so
 * the interest columns, the totals row and the phone card cannot drift apart.
 */
const INTEREST_COLOUR = 'var(--color-grey)'

export function buildRoundCells(
  s: Shareholder,
  keys: RoundColumnKey[],
  issuances: Issuance[],
): SharedCell<RoundColumnKey>[] {
  const byId = new Map(issuances.map((i) => [i.id, i]))
  return keys.map((key): SharedCell<RoundColumnKey> => {
    const parsed = parseRoundKey(key)
    const issuance = parsed ? byId.get(parsed.issuanceId) : undefined
    if (!parsed || !issuance) return { key, badge: false, text: '—' }

    const f = roundFigures(s, issuance)
    const colour = f.interest ? INTEREST_COLOUR : undefined
    // Nothing in this round is an em dash, not a nought: the holder did not take
    // part, which is a different fact from taking part for nothing (11).
    if (f.shares === 0) return { key, badge: false, text: '—', color: colour }

    switch (parsed.part) {
      case 'shares':
        return {
          key,
          badge: false,
          text: nb(f.shares),
          color: colour,
          title: f.interest
            ? `Interest only — ${issuance.name} has not closed, so these shares do not exist yet`
            : undefined,
        }
      case 'price':
        return {
          key,
          badge: false,
          text: f.blended
            ? `${f.distinct.length} prices`
            : f.price === null
              ? '—'
              : fmtKrPrice(f.price),
          color: colour,
          // The italic clause of 78: this is what *this holder* agreed, not what
          // the round charges.
          italic: f.agreed,
          title: f.blended
            ? `Two holdings at different prices: ${f.distinct.map((n) => fmtKrPrice(n)).join(' and ')}`
            : f.agreed
              ? 'A price agreed with this holder, not the round’s own'
              : undefined,
        }
      case 'amount':
        return {
          key,
          badge: false,
          // Summed from the lines, so it stays exact where the price column had
          // to give up and say "2 prices".
          text: f.distinct.length === 0 ? '—' : fmtKrSum(f.amount),
          color: colour,
          title: f.interest ? 'Subscribed, not paid' : undefined,
        }
    }
  })
}

/* -------------------------------------------------------------------------- */
/* The totals row — decision 81                                               */
/* -------------------------------------------------------------------------- */

/**
 * What the rows on screen add up to, and what its label has to admit.
 *
 * Three rules from 81, and the third is the one that makes it safe:
 *
 *  1. **Numbers, money and percentages are summed.** A column of words is left
 *     blank rather than showing a count of something else — a "144" under the
 *     Country column is a number that means nothing.
 *  2. **Issued and subscribed are summed apart.** That falls out of the columns
 *     rather than needing a rule here: a round's three columns read `purchases`
 *     or `subscriptions` by its status, never both, so no cell mixes them.
 *  3. **It totals the rows on screen**, so a search narrows it — and the label
 *     says so. A totals row that quietly sums a filtered table is a wrong
 *     number wearing a right name.
 *
 * The price column is neither summed nor averaged: decision 78 settles it —
 * *"the round's own price is what the totals row shows: a weighted average is a
 * figure nobody agreed to."* So the cell repeats the round's price, and the rows
 * above it are where a price somebody negotiated appears, in italic. A sum there
 * would be meaningless and an average would be an invention; the round's price
 * is a fact.
 */
export function totalsRow(
  shown: Shareholder[],
  all: Shareholder[],
  columns: TableKey[],
  h: HoldingMap,
  issuances: Issuance[],
): TotalsRow<TableKey> {
  const byId = new Map(issuances.map((i) => [i.id, i]))
  const positions = shown.map((s) => holdingOf(h, s))

  const sumShares = positions.reduce((n, p) => n + p.shares, 0)
  const sumOptions = positions.reduce((n, p) => n + p.options, 0)
  const sumPct = positions.reduce((n, p) => n + (p.pct ?? 0), 0)
  const sumFiles = shown.reduce((n, s) => n + (s.attachments?.length ?? 0), 0)
  const sumSubscribed = shown.reduce((n, s) => n + subscribedShares(s, issuances), 0)
  const sumUnpaid = shown.reduce((n, s) => n + unpaidExerciseShares(s), 0)

  const cells = columns.map((key): SharedCell<TableKey> => {
    const round = parseRoundKey(key)
    if (round) {
      const issuance = byId.get(round.issuanceId)
      if (!issuance) return { key, badge: false, text: '' }
      const figures = shown.map((s) => roundFigures(s, issuance))
      const shares = figures.reduce((n, f) => n + f.shares, 0)
      const amount = figures.reduce((n, f) => n + f.amount, 0)
      const open = issuance.status === 'ongoing'
      const colour = open ? INTEREST_COLOUR : undefined
      if (shares === 0) return { key, badge: false, text: '—', color: colour }
      switch (round.part) {
        case 'shares':
          return { key, badge: false, text: nb(shares), color: colour }
        case 'price':
          /**
           * Blank — and this is the one place two instructions disagree.
           *
           * Decision 78 says *"the round's own price is what the totals row
           * shows: a weighted average is a figure nobody agreed to."* The 24 Aug
           * run's A4.14 nit says the opposite about the same cell: *"the totals
           * row carries a value in the price column; blank is right (a total of
           * prices means nothing)."*
           *
           * The nit is newer and it is also the stronger argument: 78's sentence
           * is aimed at *averages*, and repeating the round's price in a row
           * labelled "Total" invites it to be read as one — a per-share figure
           * under a column of per-share figures looks summed whatever it is. 81's
           * rule for a column that cannot be totalled is a blank, and that is
           * what this is. Flagged back to the designer rather than decided here.
           */
          return { key, badge: false, text: '' }
        case 'amount':
          return {
            key,
            badge: false,
            text: amount > 0 ? fmtKrSum(amount) : '—',
            color: colour,
            title: open ? 'Subscribed, not paid' : undefined,
          }
        default:
          return { key, badge: false, text: '' }
      }
    }

    switch (key) {
      case 'shares':
        return {
          key,
          badge: false,
          text: sumShares > 0 ? nb(sumShares) : '—',
          // 79, verbatim: subscribed shares are summed apart from issued ones,
          // on their own line. "Adding the two would be the one arithmetic this
          // whole model exists to prevent."
          sub: sumSubscribed > 0 ? `${nb(sumSubscribed)} subscribed, not issued` : undefined,
        }
      case 'options':
        return {
          key,
          badge: false,
          text: sumOptions > 0 ? nb(sumOptions) : '—',
          // Summed apart, for the same reason subscribed shares are: an
          // exercised-but-unpaid grant is neither an option nor a share.
          sub: sumUnpaid > 0 ? `${nb(sumUnpaid)} exercised, awaiting payment` : undefined,
        }
      case 'ownership':
        return {
          key,
          badge: false,
          text: sumShares > 0 ? formatPct(sumPct) : '—',
          title: 'Of the shares outstanding today, after settled transfers',
        }
      case 'files':
        return { key, badge: false, text: sumFiles > 0 ? `📎 ${nb(sumFiles)}` : '—' }
      // Every remaining column holds words, a date or a status. Blank, per 81 —
      // a count of them under a column of names is a number nobody asked for.
      default:
        return { key, badge: false, text: '' }
    }
  })

  return {
    // The scope, in the row's own label. `shown` and `all` are the same length
    // only when nothing is filtered, and then the second half is dropped rather
    // than reading "144 of 144".
    label:
      shown.length === all.length
        ? `Total · ${nb(all.length)} holders`
        : `Total · ${nb(shown.length)} of ${nb(all.length)} holders shown`,
    cells,
  }
}
